import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useFormik } from 'formik';
import React from 'react';
import { toast } from 'react-toastify';
import { apiGestao } from 'services/api/api';
import { alertDefaultConfig } from 'utils/toastifyUtils';
import { object, string } from 'yup';

import { Close } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2 as Grid,
  TextField,
} from '@mui/material';

import { CloseButton } from './PasswordRecoverModal.styles';

interface IPasswordRecoverModalProps {
  open: boolean;
  onClose: () => void;
}

const PasswordRecoverModal: React.FC<IPasswordRecoverModalProps> = ({
  open,
  onClose,
}) => {
  const validationSchema = object({
    email: string()
      .email('Digite um email válido')
      .required('Email é obrigatório'),
  });

  const mutation = useMutation({
    mutationFn: (email: string) => {
      return apiGestao.post(
        `/conta/alteracao-senha?email=${encodeURIComponent(email)}`,
        undefined,
      );
    },
    onSuccess: () => {
      toast.success('Email enviado com sucesso', alertDefaultConfig);
      onClose();
    },
    onError: (error: AxiosError) => {
      toast.error(
        `Erro ao enviar email! Tente novamente mais tarde. Erro: ${error.response?.data || error.message}`,
        alertDefaultConfig,
      );
    },
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values) => {
      mutation.mutate(values.email);
    },
  });

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length === 0) {
      formik.handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      data-testid="password-recover-modal"
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <DialogTitle>Recuperação de senha</DialogTitle>
        <CloseButton
          size="small"
          onClick={onClose}
          data-testid="close-button-recover-password-modal"
        >
          <Close />
        </CloseButton>
      </Grid>
      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            autoFocus
            margin="dense"
            id="email"
            name="email"
            label="Email"
            placeholder="Digite o email cadastrado para enviarmos o link de recuperação"
            type="email"
            size="small"
            fullWidth
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={mutation.isPending}>
          Enviar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PasswordRecoverModal;
